import { ArrowLeftOutlined } from "@ant-design/icons"
import { App, Button, Empty, Flex, Skeleton, Tag, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { FilePreviewContent } from "../../components/FilePreviewModal/FilePreviewContent.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { getFormatDate } from "../../utils/date/get-date-safe.ts"
import { getEntityName } from "../../utils/user/get-entity-name.ts"
import { CommentsPanel } from "../Files/components/CommentsDrawer.tsx"
import { RejectReviewModal } from "../Files/components/RejectReviewModal.tsx"
import { readServerMessage } from "../Files/helpers/server-message.ts"
import { REVIEW_STATUS_COLOR } from "../Files/helpers/review-status-color.ts"
import {
  approveDocumentReview,
  getReviewById,
  rejectDocumentReview,
  requestChangesOnReview
} from "../Project/api/project-attachment-workflow.services.ts"
import { type DocumentReview } from "../Project/types/project-attachment-workflow.types.ts"

const { Title, Text, Paragraph } = Typography

type DecisionModal = "reject" | "requestChanges" | null

export function DocumentReviewWorkspacePage() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { id } = useParams<{ id: string }>()

  const [review, setReview] = useState<DocumentReview | null | undefined>(
    undefined
  )
  const [loadFailed, setLoadFailed] = useState(false)
  const [approving, setApproving] = useState(false)
  const [decisionModal, setDecisionModal] = useState<DecisionModal>(null)
  const [reloadCount, setReloadCount] = useState(0)

  useEffect(() => {
    generateDocumentTitle(t("documentReviews.title"))
  }, [t])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setReview(undefined)
    setLoadFailed(false)
    void getReviewById(id).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        setReview(null)
        setLoadFailed(true)
        return
      }
      setReview(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [id, reloadCount])

  const retry = () => setReloadCount((count) => count + 1)

  const handleApprove = useCallback(async () => {
    if (!review) return
    setApproving(true)
    const response = await approveDocumentReview(review.id)
    setApproving(false)
    if (!response.ok || !response.data) {
      message.error(
        readServerMessage(response.data) ?? t("files.review.approveFailed")
      )
      return
    }
    message.success(t("files.review.approveSucceeded"))
    setReview(response.data.details.data)
  }, [review, message, t])

  const handleReject = useCallback(
    async (reason: string) => {
      if (!review) return
      const response = await rejectDocumentReview(review.id, reason)
      if (!response.ok || !response.data) {
        message.error(
          readServerMessage(response.data) ?? t("files.review.rejectFailed")
        )
        return
      }
      message.success(t("files.review.rejectSucceeded"))
      setReview(response.data.details.data)
      setDecisionModal(null)
    },
    [review, message, t]
  )

  const handleRequestChanges = useCallback(
    async (reason: string) => {
      if (!review) return
      const response = await requestChangesOnReview(review.id, reason)
      if (!response.ok || !response.data) {
        message.error(
          readServerMessage(response.data) ??
            t("files.review.requestChangesFailed")
        )
        return
      }
      message.success(t("files.review.requestChangesSucceeded"))
      setReview(response.data.details.data)
      setDecisionModal(null)
    },
    [review, message, t]
  )

  if (review === undefined) {
    return (
      <Flex vertical gap="middle" className="size-full p-4">
        <Skeleton.Input active size="small" style={{ width: 200 }} />
        <Skeleton active paragraph={{ rows: 8 }} />
      </Flex>
    )
  }

  if (loadFailed || review === null) {
    return (
      <Flex
        vertical
        gap="middle"
        align="center"
        justify="center"
        className="size-full"
      >
        <Empty description={t("files.review.loadFailed")}>
          <Button onClick={retry}>{t("files.review.retry")}</Button>
        </Empty>
      </Flex>
    )
  }

  const reviewerName = review.reviewer ? getEntityName(review.reviewer) : null
  const requestedByName = review.requested_by
    ? getEntityName(review.requested_by)
    : null

  return (
    <Flex vertical gap="middle" className="size-full overflow-auto p-1">
      <Flex vertical gap={4}>
        <Link to={RoutesPaths.document_reviews.list} className="w-fit">
          <Flex align="center" gap={6}>
            <ArrowLeftOutlined />
            <Text>{t("documentReviews.title")}</Text>
          </Flex>
        </Link>
        <Flex align="center" gap={12} wrap="wrap">
          <Title level={3} className="!mb-0 break-all">
            {review.file_name}
          </Title>
          <Tag color={REVIEW_STATUS_COLOR[review.status]}>
            {t(`files.review.status.${review.status}`)}
          </Tag>
        </Flex>
        <Flex align="center" gap={12} wrap="wrap">
          <Link to={RoutesPaths.project.view(review.project_id).files()}>
            {review.project_name}
          </Link>
          <Text type="secondary">·</Text>
          <Link
            to={RoutesPaths.project.view(review.project_id).files({
              fileId: review.attachment
            })}
          >
            {t("documentReviews.viewInFiles")}
          </Link>
        </Flex>
      </Flex>

      <Flex gap="middle" wrap="wrap" align="stretch" className="flex-1">
        <div className="min-w-[320px] flex-[2] rounded-md border border-border p-3">
          <FilePreviewContent
            fileUrl={review.file}
            name={review.file_name}
            maxHeight="70vh"
          />
        </div>

        <Flex vertical gap="middle" className="min-w-[300px] flex-[1]">
          <Flex
            vertical
            gap="middle"
            className="rounded-md border border-border p-3"
          >
            <Flex vertical gap={4}>
              <Text type="secondary">
                {t("fileViewer.detailsPanel.versionShort", {
                  version: review.attachment_version
                })}
              </Text>
            </Flex>

            {reviewerName && (
              <Flex vertical gap={4}>
                <Text type="secondary">{t("files.review.reviewer")}</Text>
                <Text>{reviewerName}</Text>
              </Flex>
            )}

            {requestedByName && (
              <Flex vertical gap={4}>
                <Text type="secondary">{t("files.review.requestedBy")}</Text>
                <Text>{requestedByName}</Text>
              </Flex>
            )}

            {review.due_date && (
              <Flex vertical gap={4}>
                <Text type="secondary">{t("files.review.dueDate")}</Text>
                <Text>{getFormatDate(review.due_date) ?? review.due_date}</Text>
              </Flex>
            )}

            {review.message && (
              <Flex vertical gap={4}>
                <Text type="secondary">{t("files.review.message")}</Text>
                <Paragraph className="!mb-0">{review.message}</Paragraph>
              </Flex>
            )}

            {(review.status === "rejected" ||
              review.status === "changes_requested") &&
              review.rejection_reason && (
                <Flex vertical gap={4}>
                  <Text type="secondary">
                    {review.status === "rejected"
                      ? t("files.review.rejectReason")
                      : t("files.review.changesRequestedReason")}
                  </Text>
                  <Paragraph className="!mb-0">
                    {review.rejection_reason}
                  </Paragraph>
                </Flex>
              )}

            {review.status === "pending" && (
              <Flex gap="small" wrap="wrap">
                <Button
                  type="primary"
                  loading={approving}
                  onClick={() => void handleApprove()}
                >
                  {t("files.review.approve")}
                </Button>
                <Button onClick={() => setDecisionModal("requestChanges")}>
                  {t("files.review.requestChanges")}
                </Button>
                <Button danger onClick={() => setDecisionModal("reject")}>
                  {t("files.review.reject")}
                </Button>
              </Flex>
            )}
          </Flex>

          <Flex
            vertical
            className="min-h-[300px] flex-1 rounded-md border border-border p-3"
          >
            <Text type="secondary" className="mb-2 uppercase">
              {t("files.comments.viewAction")}
            </Text>
            <CommentsPanel attachmentId={review.attachment} />
          </Flex>
        </Flex>
      </Flex>

      {decisionModal === "reject" && (
        <RejectReviewModal
          fileName={review.file_name}
          onClose={() => setDecisionModal(null)}
          onSubmit={handleReject}
        />
      )}

      {decisionModal === "requestChanges" && (
        <RejectReviewModal
          fileName={review.file_name}
          title={t("files.review.requestChangesTitle")}
          submitText={t("files.review.requestChangesSubmit")}
          reasonLabel={t("files.review.requestChangesReason")}
          placeholder={t("files.review.requestChangesPlaceholder")}
          danger={false}
          onClose={() => setDecisionModal(null)}
          onSubmit={handleRequestChanges}
        />
      )}
    </Flex>
  )
}
